import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Network, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function IPCalculator() {
  const navigate = useNavigate();
  const [cidr, setCidr] = useState("");
  const [networkInfo, setNetworkInfo] = useState(null);
  const [checkIp, setCheckIp] = useState("");
  const [rangeResult, setRangeResult] = useState(null);
  const [snippetName, setSnippetName] = useState("my_subnet");
  const [snippetPlatform, setSnippetPlatform] = useState("terraform");
  const [snippet, setSnippet] = useState("");

  const calculateNetwork = async () => {
    if (!cidr.trim()) {
      toast.error("Please enter a CIDR notation");
      return;
    }
    try {
      const response = await axios.post(`${API}/ip/calculate`, { cidr });
      setNetworkInfo(response.data);
      toast.success("Network calculated successfully!");
    } catch (error) {
      toast.error("Invalid CIDR notation");
    }
  };

  const checkRange = async () => {
    if (!cidr.trim() || !checkIp.trim()) {
      toast.error("Please enter both CIDR and IP address");
      return;
    }
    try {
      const response = await axios.post(`${API}/ip/check-range`, {
        cidr,
        ip_address: checkIp
      });
      setRangeResult(response.data);
    } catch (error) {
      toast.error("Invalid input");
    }
  };

  const generateSnippet = async () => {
    if (!cidr.trim()) {
      toast.error("Please calculate network first");
      return;
    }
    try {
      const response = await axios.post(`${API}/ip/generate-snippet`, {
        cidr,
        resource_name: snippetName,
        platform: snippetPlatform
      });
      setSnippet(response.data.snippet);
      toast.success(`${snippetPlatform} snippet generated!`);
    } catch (error) {
      toast.error("Failed to generate snippet");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#0f0f12] to-[#0a0a0b]">
      {/* Header */}
      <div className="border-b border-zinc-800/50 backdrop-blur-xl bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-home-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>IP Calculator (CIDR)</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Input Section */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Network Calculator</h2>
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <Label htmlFor="cidr" className="text-zinc-400 mb-2 block">CIDR Notation</Label>
              <Input
                data-testid="cidr-input"
                id="cidr"
                value={cidr}
                onChange={(e) => setCidr(e.target.value)}
                placeholder="e.g., 192.168.1.0/24"
                className="bg-zinc-800 border-zinc-700 text-white font-mono"
              />
            </div>
            <Button
              data-testid="calculate-btn"
              onClick={calculateNetwork}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
            >
              Calculate
            </Button>
          </div>
        </Card>

        {/* Results */}
        {networkInfo && (
          <div className="space-y-6">
            {/* Network Info */}
            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Network Information</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                  <div className="text-sm text-zinc-400 mb-1">Network Address</div>
                  <div className="text-lg font-semibold text-white font-mono">{networkInfo.network_address}</div>
                </div>
                <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                  <div className="text-sm text-zinc-400 mb-1">Broadcast Address</div>
                  <div className="text-lg font-semibold text-white font-mono">{networkInfo.broadcast_address}</div>
                </div>
                <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                  <div className="text-sm text-zinc-400 mb-1">Netmask</div>
                  <div className="text-lg font-semibold text-white font-mono">{networkInfo.netmask}</div>
                </div>
                <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                  <div className="text-sm text-zinc-400 mb-1">Wildcard Mask</div>
                  <div className="text-lg font-semibold text-white font-mono">{networkInfo.wildcard_mask}</div>
                </div>
                <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                  <div className="text-sm text-zinc-400 mb-1">Total Hosts</div>
                  <div className="text-lg font-semibold text-violet-400">{networkInfo.total_hosts}</div>
                </div>
                <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                  <div className="text-sm text-zinc-400 mb-1">Prefix Length</div>
                  <div className="text-lg font-semibold text-violet-400">/{networkInfo.prefix_length}</div>
                </div>
              </div>
              
              <div className="mt-6">
                <div className="text-sm text-zinc-400 mb-2">IP Range</div>
                <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700">
                  <div className="font-mono text-white">{networkInfo.ip_range}</div>
                </div>
              </div>

              {networkInfo.hosts_preview && networkInfo.hosts_preview.length > 0 && (
                <div className="mt-6">
                  <div className="text-sm text-zinc-400 mb-2">Host Preview (First 100)</div>
                  <div className="bg-zinc-800 p-4 rounded-lg border border-zinc-700 max-h-48 overflow-y-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-sm text-zinc-300">
                      {networkInfo.hosts_preview.map((host, i) => (
                        <div key={i}>{host}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Tabs for additional features */}
            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <Tabs defaultValue="check" className="w-full">
                <TabsList className="bg-zinc-800 border-zinc-700">
                  <TabsTrigger value="check">Check IP Range</TabsTrigger>
                  <TabsTrigger value="snippet">Generate IaC Snippet</TabsTrigger>
                </TabsList>

                <TabsContent value="check" className="space-y-4">
                  <div className="flex items-end space-x-4">
                    <div className="flex-1">
                      <Label htmlFor="check-ip" className="text-zinc-400 mb-2 block">IP Address to Check</Label>
                      <Input
                        data-testid="check-ip-input"
                        id="check-ip"
                        value={checkIp}
                        onChange={(e) => setCheckIp(e.target.value)}
                        placeholder="e.g., 192.168.1.50"
                        className="bg-zinc-800 border-zinc-700 text-white font-mono"
                      />
                    </div>
                    <Button
                      data-testid="check-range-btn"
                      onClick={checkRange}
                      className="bg-violet-600 hover:bg-violet-700 text-white"
                    >
                      Check Range
                    </Button>
                  </div>

                  {rangeResult && (
                    <div className="bg-zinc-800/50 p-6 rounded-lg border border-zinc-700">
                      <div className="flex items-center space-x-3">
                        {rangeResult.in_range ? (
                          <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                        ) : (
                          <XCircle className="w-8 h-8 text-red-500" />
                        )}
                        <div>
                          <div className="text-lg font-bold text-white">
                            {rangeResult.in_range ? "IP is in range" : "IP is NOT in range"}
                          </div>
                          <div className="text-sm text-zinc-400 mt-1">
                            {rangeResult.ip_address} {rangeResult.in_range ? "∈" : "∉"} {rangeResult.network}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="snippet" className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="snippet-name" className="text-zinc-400 mb-2 block">Resource Name</Label>
                      <Input
                        data-testid="snippet-name-input"
                        id="snippet-name"
                        value={snippetName}
                        onChange={(e) => setSnippetName(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="platform" className="text-zinc-400 mb-2 block">Platform</Label>
                      <select
                        data-testid="platform-select"
                        id="platform"
                        value={snippetPlatform}
                        onChange={(e) => setSnippetPlatform(e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                      >
                        <option value="terraform">Terraform</option>
                        <option value="cloudformation">CloudFormation</option>
                      </select>
                    </div>
                  </div>
                  <Button
                    data-testid="generate-snippet-btn"
                    onClick={generateSnippet}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    Generate Snippet
                  </Button>

                  {snippet && (
                    <div className="relative">
                      <Button
                        data-testid="copy-snippet-btn"
                        onClick={() => copyToClipboard(snippet)}
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 text-zinc-400 hover:text-white z-10"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <pre className="bg-zinc-800 p-4 rounded-lg border border-zinc-700 overflow-x-auto text-sm text-zinc-300">
                        {snippet}
                      </pre>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}